import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import logo from "@/assets/logo.png";

interface FooterProps {
  hideInfoSection?: boolean;
}

const Footer = ({ hideInfoSection = false }: FooterProps) => {
  return <footer className="bg-card border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 py-12 sm:py-14 md:py-16">
        {/* Info Section */}
        {!hideInfoSection && (
          <div className="text-center mb-12 sm:mb-14 md:mb-16 pb-8 sm:pb-10 md:pb-12 border-b border-border">
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-4 text-foreground tracking-wide font-bold px-4">
              NO-NONSENSE SERVICE
            </h2>
            <p className="text-foreground/70 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed px-4">
              Een plek waar je je thuis voelt, met aandacht voor details, kwaliteit en een ontspannen moment voor jezelf, met een koffie of biertje tijdens een goed gesprek.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-10 md:mb-12">
          {/* Brand */}
          <div className="text-center sm:text-left">
            <Link to="/">
              <img src={logo} alt="Bo Barbers" width="49" height="64" className="h-16 w-auto invert mb-4 mx-auto sm:mx-0" />
            </Link>
            <p className="text-muted-foreground mb-4 sm:mb-6 text-sm leading-relaxed font-light max-w-xs mx-auto sm:mx-0">
              Traditioneel vakmanschap met een moderne twist. Waar kwaliteit en stijl samenkomen.
            </p>
            <div className="flex gap-3 justify-center sm:justify-start">
              <a href="https://www.instagram.com/bobarbersutrecht/" target="_blank" rel="noopener noreferrer" aria-label="Volg ons op Instagram" className="w-9 h-9 rounded-full border border-primary/30 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all text-primary">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="https://www.facebook.com/bobarbersutrecht/?locale=nl_NL" target="_blank" rel="noopener noreferrer" aria-label="Volg ons op Facebook" className="w-9 h-9 rounded-full border border-primary/30 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all text-primary">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://nl.linkedin.com/in/bo-van-lavieren-41711017a" target="_blank" rel="noopener noreferrer" aria-label="Bekijk ons LinkedIn profiel" className="w-9 h-9 rounded-full border border-primary/30 flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-all text-primary">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="text-center sm:text-left">
            <div className="font-semibold text-primary mb-4 sm:mb-6 uppercase tracking-wider text-xs">VERKEN</div>
            <ul className="space-y-2 sm:space-y-3">
              <li><Link to="/" className="text-muted-foreground hover:text-primary transition-colors text-sm">Home</Link></li>
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors text-sm">Over Ons</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors text-sm">Services</Link></li>
              <li><Link to="/products" className="text-muted-foreground hover:text-primary transition-colors text-sm">Producten</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Working Hours */}
          <div className="text-center sm:text-left">
            <div className="font-semibold text-primary mb-4 sm:mb-6 uppercase tracking-wider text-xs">OPENINGSTIJDEN</div>
            <ul className="space-y-2 sm:space-y-3 text-sm font-light">
              <li className="text-muted-foreground">
                <div>Maandag - Vrijdag</div>
                <div className="text-primary">08:30 - 17:30</div>
              </li>
              <li className="text-muted-foreground">
                <div>Donderdag</div>
                <div className="text-primary">08:30 - 19:00</div>
              </li>
              <li className="text-muted-foreground">
                <div>Zaterdag</div>
                <div className="text-primary">09:00 - 15:00</div>
              </li>
              <li className="text-muted-foreground">
                <div>Zondag</div>
                <div className="text-primary">Gesloten</div>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div className="text-center sm:text-left">
            <div className="font-semibold text-primary mb-4 sm:mb-6 uppercase tracking-wider text-xs">SERVICES</div>
            <ul className="space-y-2 sm:space-y-3">
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors text-sm">Herenkapsels</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors text-sm">Baardverzorging</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors text-sm">Combinatie</Link></li>
              <li><Link to="/services" className="text-muted-foreground hover:text-primary transition-colors text-sm">Hot Towel Shave</Link></li>
            </ul>
          </div>
        </div>


        {/* Copyright */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-muted-foreground font-light px-4">
          <p className="text-center md:text-left">
            Copyright © 2026 Bo Barbers. Alle Rechten Voorbehouden | Powered by{" "}
            <a href="https://processtudio.nl" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              Processtudio
            </a>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-8">
            <Link to="/privacy" className="hover:text-primary transition-colors uppercase tracking-wider text-center">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-primary transition-colors uppercase tracking-wider text-center">Terms & Services</Link>
          </div>
        </div>
      </div>
    </footer>;
};
export default Footer;